import moment from 'moment';
import _ from 'lodash';

export const EMPTY_LABEL = '(empty)';

export const TOTAL_LABEL = 'Total Result';

const toLabel = (value) => {
  const label = value ? String(value).trim() : '';

  return label || EMPTY_LABEL;
};

const emptyLast = (label) => (label === EMPTY_LABEL ? 1 : 0);

export const buildPivot = (rows = [], {
  accumulateData, sortByMonth, withTotalRow, colKey = 'source',
} = {}) => {
  const empty = { columns: [], data: [] };

  if (!Array.isArray(rows) || !rows.length) return empty;

  const columns = [];
  const columnTotals = {};

  const groupedData = _.groupBy(rows, (row) => {
    if (sortByMonth) return `${toLabel(row.month)}${row.year ? ` ${row.year}` : ''}`;

    return colKey ? toLabel(row[colKey]) : '';
  });

  let pivotRows = _.map(groupedData, (val, key) => {
    const rowData = {};
    let total = 0;

    val.forEach((v) => {
      const columnTitle = toLabel(v[colKey]);
      rowData[columnTitle] = v.total;
      total += v.total;

      columnTotals[columnTitle] = (columnTotals[columnTitle] || 0) + v.total;

      if (!columns.includes(columnTitle)) columns.push(columnTitle);
    });

    return {
      label: key,
      ...rowData,
      total,
    };
  });

  if (accumulateData) {
    const accumulatedRow = {};

    pivotRows.forEach((row) => {
      accumulatedRow[row.label] = (accumulatedRow[row.label] || 0) + row.total;
    });

    pivotRows = [{ ...accumulatedRow, total: _.sum(Object.values(accumulatedRow)) }];
  }

  const data = _.sortBy(pivotRows, sortByMonth
    ? (row) => moment(row.label, 'MMM YYYY').valueOf()
    : [(row) => emptyLast(row.label), 'label']);

  if (withTotalRow) {
    data.push({
      ...columnTotals,
      label: TOTAL_LABEL,
      total: _.sumBy(pivotRows, 'total'),
      isTotal: true,
    });
  }

  return { columns: _.sortBy(columns, [emptyLast, (column) => column]), data };
};

export const pivotRows = (pivot) => (pivot?.data || []).filter((row) => !row.isTotal);

export const pivotTotalRow = (pivot) => (pivot?.data || []).find((row) => row.isTotal) || {};

export const isEmptyPivot = (pivot) => !pivot?.columns?.length || !pivotRows(pivot).length;

export const pivotToChart = (pivot, {
  axis = 'row', totals = false, withTotal = false, seriesName = 'Count',
} = {}) => {
  if (isEmptyPivot(pivot)) return { categories: [], series: [] };

  const { columns } = pivot;
  const rows = pivotRows(pivot);
  const totalRow = pivotTotalRow(pivot);

  if (axis === 'column') {
    return {
      categories: columns,
      series: totals
        ? [{ name: seriesName, data: columns.map((column) => totalRow[column] || 0) }]
        : rows.map((row) => ({
          name: row.label,
          data: columns.map((column) => row[column] || 0),
        })),
    };
  }

  const categories = rows.map((row) => row.label);

  if (totals) {
    return {
      categories: withTotal ? [...categories, TOTAL_LABEL] : categories,
      series: [{
        name: seriesName,
        data: withTotal
          ? [...rows.map((row) => row.total), totalRow.total || 0]
          : rows.map((row) => row.total),
      }],
    };
  }

  return {
    categories: withTotal ? [...categories, TOTAL_LABEL] : categories,
    series: columns.map((column) => ({
      name: column,
      data: withTotal
        ? [...rows.map((row) => row[column] || 0), totalRow[column] || 0]
        : rows.map((row) => row[column] || 0),
    })),
  };
};

const toNumber = (value) => +(value || 0);

const summaryCounts = (node) => ({
  memberCount: toNumber(node.memberCount),
  statusCount: toNumber(node.statusCount),
  actionNeededByCount: toNumber(node.actionNeededByCount),
  finalOutcomeCount: toNumber(node.finalOutcomeCount),
  sourceCount: toNumber(node.sourceCount),
});

export const buildSummaryRows = (data) => {
  const rows = [];

  (data?.rows || []).forEach((lob) => {
    rows.push({
      level: 0,
      lob: toLabel(lob.lob),
      filters: { lob: toLabel(lob.lob) },
      ...summaryCounts(lob),
    });

    (lob.outcomes || []).forEach((outcome) => {
      rows.push({
        level: 1,
        finalOutcome: toLabel(outcome.finalOutcome),
        filters: { lob: toLabel(lob.lob), finalOutcome: toLabel(outcome.finalOutcome) },
        ...summaryCounts(outcome),
      });

      (outcome.sources || []).forEach((source) => {
        rows.push({
          level: 2,
          source: toLabel(source.source),
          filters: {
            lob: toLabel(lob.lob),
            finalOutcome: toLabel(outcome.finalOutcome),
            source: toLabel(source.source),
          },
          ...summaryCounts(source),
        });
      });
    });
  });

  if (rows.length && data?.grandTotal) {
    rows.push({
      level: 0, lob: TOTAL_LABEL, isTotal: true, filters: {}, ...summaryCounts(data.grandTotal),
    });
  }

  return rows;
};
